import type { PostStatus, TargetStatus } from '@postpilot/core/domain';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { STATUS_CONFIG } from '@/components/ui/status-config';
import { cn } from '@/lib/utils';

const badgeVariants = cva(
  cn(
    'inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-medium',
    'whitespace-nowrap transition-colors',
    '[&_svg]:size-3 [&_svg]:shrink-0',
  ),
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground border-transparent',
        secondary: 'bg-secondary text-secondary-foreground border-transparent',
        outline: 'text-foreground',
        destructive: 'bg-destructive text-destructive-foreground border-transparent',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {}

export function Badge({ className, variant, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ variant }), className)} {...props} />;
}

/** The status pill used throughout the queue and the dashboard. */
export function StatusBadge({
  status,
  className,
}: {
  status: PostStatus | TargetStatus;
  className?: string;
}) {
  const config = STATUS_CONFIG[status];
  const Icon = config.icon;

  return (
    <span className={cn(badgeVariants({ variant: 'outline' }), config.className, className)}>
      {/* Only the in-flight state spins. A spinner on a finished state would
          imply work is still happening. */}
      <Icon className={cn(status === 'processing' && 'animate-spin')} aria-hidden />
      {config.label}
    </span>
  );
}
